import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import BusinessIcon from '@mui/icons-material/Business';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import FactoryIcon from '@mui/icons-material/Factory';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Filejet, FilejetProvider, LruCache } from '@filejet/sdk/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Admin, defaultDarkTheme, defaultLightTheme, Layout, Resource } from 'react-admin';
import { dataProvider } from './dataProvider';
import { GroupEdit, GroupList } from './groups';
import { OrderList } from './orders';
import { OrderShow } from './orderShow';
import { LoyaltyCreate, LoyaltyEdit, LoyaltyList } from './loyalty';
import { ContentCreate, ContentEdit, ContentList } from './content';
import { StaffCreate, StaffEdit, StaffList } from './staff';
import { OfferCreate, OfferEdit, OfferList } from './offers';
import { i18nProvider } from './i18n';
import { Dashboard } from './dashboard';
import { authProvider } from './authProvider';
import { AdminMenu } from './menu';
import { AdminAppBar } from './appBar';
import { themeStore } from './theme';
import { LoginPage } from './LoginPage';
import { OrganizationList } from './organizations';
import { OrganizationEdit, OrganizationShow } from './organizationShow';
import { ProducerCreate, ProducerEdit, ProducerList } from './producers';
import { UserShow } from './userShow';
import { ProductEdit } from './productEdit';
import { ProductCreate } from './productCreate';
import { ProductList } from './products';
import { UserList } from './users';
import { AdminCreate, AdminEdit, AdminList } from './admins';

/**
 * Orders is the default resource, so signing in lands on the queue. That is
 * where the team spends the day; everything else is occasional.
 */
/**
 * FileJet, configured once. The domain is the storefront's storage id - images
 * are the same files the shop serves, so the admin shows exactly what a
 * customer sees rather than a differently processed copy.
 */
const filejet = new Filejet({
  domain: import.meta.env['VITE_FILEJET_DOMAIN'] ?? 'hz0ur4.5gcdn.net',
  Img: {
    dpiScale: [1, 2],
    placeholderNode: <div style={{ background: '#f1f1f1', width: '100%', height: '100%' }} />,
    errorNode: <div style={{ background: '#f7e6e6', width: '100%', height: '100%' }} />
  },
  // Required by the SDK even though nothing here passes a thumbhash: the
  // product images predate them.
  ThumbhashImg: { cache: new LruCache({ maxSize: 100 }), intersectRootMargin: 200 }
});

/** The default layout, with our own grouped navigation and branded bar. */
const AdminLayout = (props: React.ComponentProps<typeof Layout>) => (
  <Layout {...props} menu={AdminMenu} appBar={AdminAppBar} />
);

function App() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      // Without it react-admin renders the dashboard before checking for a
      // session, and the dashboard's first request goes out with no token.
      requireAuth
      loginPage={LoginPage}
      i18nProvider={i18nProvider}
      dashboard={Dashboard}
      layout={AdminLayout}
      // Light or dark is remembered across sessions, not just this tab.
      store={themeStore}
      // Both themes declared, so the app bar offers the switch at all - and
      // the store above is what makes the choice outlive the tab.
      theme={defaultLightTheme}
      darkTheme={defaultDarkTheme}
      title="CleanTonery"
      disableTelemetry
    >
      {/* A function child so react-admin re-evaluates this when the signed-in
          account changes. `admins` is registered only for the owner, which
          means an ordinary administrator has no route to it at all - typing
          the URL lands on the dashboard rather than on a screen that loads and
          then fails. */}
      {(permissions: string) => (
        <>
          <Resource name="orders" list={OrderList} show={OrderShow} icon={ShoppingCartIcon} />
          <Resource name="products" list={ProductList} edit={ProductEdit} create={ProductCreate} icon={CategoryIcon} />
          <Resource name="users" list={UserList} show={UserShow} icon={PeopleIcon} />
          <Resource name="groups" list={GroupList} edit={GroupEdit} icon={GroupWorkIcon} />
          <Resource
            name="producers"
            list={ProducerList}
            edit={ProducerEdit}
            create={ProducerCreate}
            icon={FactoryIcon}
          />
          <Resource
            name="organizations"
            list={OrganizationList}
            show={OrganizationShow}
            edit={OrganizationEdit}
            icon={BusinessIcon}
          />
          <Resource
            name="loyalty"
            list={LoyaltyList}
            edit={LoyaltyEdit}
            create={LoyaltyCreate}
            icon={CardGiftcardIcon}
          />
          <Resource name="offers" list={OfferList} edit={OfferEdit} create={OfferCreate} icon={CampaignIcon} />
          <Resource name="content" list={ContentList} edit={ContentEdit} create={ContentCreate} icon={ArticleIcon} />
          <Resource name="staff" list={StaffList} edit={StaffEdit} create={StaffCreate} icon={ContactPhoneIcon} />

          {permissions === 'owner' && (
            <Resource
              name="admins"
              list={AdminList}
              edit={AdminEdit}
              create={AdminCreate}
              icon={AdminPanelSettingsIcon}
            />
          )}
        </>
      )}
    </Admin>
  );
}

const container = document.getElementById('root');
if (container == null) throw new Error('No #root element to mount into.');

createRoot(container).render(
  <React.StrictMode>
    <FilejetProvider filejet={filejet}>
      <App />
    </FilejetProvider>
  </React.StrictMode>
);
